import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { useThreadList } from '@/features/agent/useAgentRuntime'

type ThreadList = ReturnType<typeof useThreadList>

export interface AgentThreadSelection {
  /** The thread shown in the main panel. */
  activeThreadId: string | undefined
  setActiveThreadId: (threadId: string | undefined) => void
  /** Prompt captured from the URL, fired into a fresh thread then cleared. */
  pendingPrompt: string | undefined
  clearPendingPrompt: () => void
  /** Create a thread and select it. */
  handleCreate: () => Promise<void>
  /** Delete a thread; re-selects a remaining thread if the active one went away. */
  handleDelete: (threadId: string) => Promise<void>
  handleRename: (threadId: string, title: string) => void
}

/**
 * Owns the thread-selection state machine for the agent page: which thread is
 * shown in the main panel, the one-shot prompt captured from the URL, and the
 * create/delete/rename handlers.
 *
 * Data fetching (the thread list + mutations) stays in the page via
 * `useThreadList`; its result is passed in so this hook stays focused on
 * selection/orchestration logic.
 */
export function useAgentThreadSelection(threadList: ThreadList): AgentThreadSelection {
  const { threads, isLoading, create, remove, rename } = threadList
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeThreadId, setActiveThreadId] = useState<string | undefined>(
    searchParams.get('thread') ?? undefined
  )
  // Captured once on mount so we can fire it into a fresh thread.
  const [pendingPrompt, setPendingPrompt] = useState<string | undefined>(
    searchParams.get('prompt') ?? undefined
  )

  // Clear ?thread= / ?prompt= from URL once consumed
  useEffect(() => {
    if (searchParams.get('thread') || searchParams.get('prompt')) {
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Auto-select a thread once loaded (if none specified).
  // - With a pending prompt: force a fresh thread so the user's message
  //   isn't appended onto an existing conversation.
  // - Otherwise: pick the most recent CHAT thread — run threads are
  //   internal artifacts and the wrong landing default even when newest.
  useEffect(() => {
    if (isLoading || activeThreadId) return
    if (pendingPrompt) {
      if (!create.isPending) {
        void (async () => {
          const thread = await create.mutateAsync()
          setActiveThreadId(thread.id)
        })()
      }
      return
    }
    const candidate = threads.find(t => t.kind === 'chat') ?? threads[0]
    if (candidate) setActiveThreadId(candidate.id)
  }, [isLoading, threads, activeThreadId, pendingPrompt, create])

  async function handleCreate() {
    const thread = await create.mutateAsync()
    setActiveThreadId(thread.id)
  }

  async function handleDelete(threadId: string) {
    await remove.mutateAsync(threadId)
    if (activeThreadId === threadId) {
      const remaining = threads.filter(t => t.id !== threadId)
      setActiveThreadId(remaining[0]?.id)
    }
  }

  function handleRename(threadId: string, title: string) {
    rename.mutate({ threadId, title })
  }

  return {
    activeThreadId,
    setActiveThreadId,
    pendingPrompt,
    clearPendingPrompt: () => setPendingPrompt(undefined),
    handleCreate,
    handleDelete,
    handleRename,
  }
}
